import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Loader } from '@/components/loader';
import { useToast } from '@/components/ui/use-toast';
import StaffAPI from '@/api/staff.api';
import authService from '@/lib/auth';

const fullName = u => [u.firstName, u.lastName].filter(Boolean).join(' ').trim();

export default function StaffList() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  // Same lookup pattern as the org-list page so we can pin the signed-in user.
  const meId = authService.state?.tokenDetails?.id || authService.state?.user?.id || null;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await StaffAPI.list();
      if (res.error) {
        toast({
          title: 'Failed to load staff',
          description: res.data?.message || res.error.message || 'Unexpected error',
          variant: 'destructive',
        });
        return;
      }
      setUsers(res.data?.users ?? []);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  // The /v1/staff endpoint doesn't take a search param (yet) — the staff org is
  // small, so we filter client-side over email + name. The signed-in user's row
  // is pinned to the top so it's always one click away.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const matches = u =>
      !q || (u.email || '').toLowerCase().includes(q) || fullName(u).toLowerCase().includes(q);

    const matched = users.filter(matches);
    if (!meId) return matched;

    const isMe = u => String(u.id ?? u._id) === String(meId);
    const me = matched.find(isMe);
    if (!me) return matched;

    return [me, ...matched.filter(u => !isMe(u))];
  }, [users, search, meId]);

  return (
    <Card>
      <CardHeader>
        <div>
          <CardTitle>Manage C8 user subscriptions</CardTitle>
          <CardDescription>
            Members of c8health staff. Click a row to manage their subscriptions.
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex gap-2 mb-4">
          <Input
            placeholder="Filter by email, first or last name"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        {loading ? (
          <Loader />
        ) : (
          <>
            <div className="text-sm text-muted-foreground mb-2">
              {filtered.length} staff member(s)
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Name</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={2} className="text-center text-muted-foreground">
                      No staff members found.
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map(u => {
                    const isMe = meId && String(u.id ?? u._id) === String(meId);
                    return (
                      <TableRow
                        key={u.id}
                        onClick={() => navigate(`/dashboard/staff/${u.id}`)}
                        className={`cursor-pointer hover:bg-muted/50 ${isMe ? 'bg-muted/30' : ''}`}>
                        <TableCell className="font-mono text-xs">{u.email}</TableCell>
                        <TableCell>
                          <span className="inline-flex items-center gap-2">
                            {fullName(u) || '—'}
                            {isMe && (
                              <Badge variant="default" className="text-xs">
                                You
                              </Badge>
                            )}
                          </span>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </>
        )}
      </CardContent>
    </Card>
  );
}
